import { supabase } from './supabase';
import { addCaregiver } from './axiscare';

// Extracted from OnboardingPipeline.js's createInAxisCare (2026-08-08) so the
// same push logic backs both the normal onboarding-pipeline flow AND the
// Personnel.js "Quick Add" fast-track path, instead of the two drifting
// apart. Resolves the AxisCare "class" mapping via role_external_mappings
// (system='axiscare'), then pushes a new caregiver record to AxisCare.
//
// Does NOT touch the staff row — callers decide what to persist
// (axiscare_id/axiscare_created_at, plus any onboarding_status transition
// specific to their own flow) and how to log/notify, since that differs
// between the pipeline and the quick-create path.
//
// Throws on failure (mirrors the original inline behavior) — callers catch
// and surface the message.
export async function pushCaregiverToAxisCare(staff) {
  // Prefer the structured first_name/last_name; fall back to splitting
  // full_name for older records that predate that column.
  const [splitFirst, ...splitRest] = (staff.full_name || '').split(' ');

  let axisClass;
  if (staff.position_code) {
    const { data: mapping } = await supabase.from('role_external_mappings').select('external_code')
      .eq('system', 'axiscare').eq('role_code', staff.position_code)
      .eq('is_lead', !!staff.is_lead)
      .eq('caregiver_relationship', staff.caregiver_relationship || 'any')
      .maybeSingle();
    axisClass = mapping?.external_code;
  }

  const { data, error } = await addCaregiver({
    firstName: staff.first_name || splitFirst || '',
    lastName: staff.last_name || splitRest.join(' '),
    goesBy: staff.goes_by || undefined,
    personalEmail: staff.email || undefined,
    mobilePhone: staff.phone || undefined,
    payRate: staff.pay_rate || undefined,
    dateOfBirth: staff.date_of_birth || undefined,
    startDate: staff.start_date || undefined,
    mailingAddress: (staff.address || staff.city || staff.zip) ? {
      streetAddress1: staff.address || undefined,
      city: staff.city || undefined,
      state: staff.state || undefined,
      postalCode: staff.zip || undefined,
    } : undefined,
    ...(axisClass ? { classes: [{ code: axisClass }] } : {}),
  });
  if (error) throw new Error(error.message || 'AxisCare request failed');
  const newId = data?.id || data?.results?.id || data?.caregiver?.id;
  if (!newId) throw new Error('AxisCare responded but no caregiver ID was found in the response — the payload field names may need adjusting.');
  return String(newId);
}
